import React, { useEffect, useState } from 'react';
import { X, Mail, Phone, MapPin, GraduationCap, Clock, CalendarClock, FileText, User, PenLine } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { ApplicantProfile, EducationStatusLabels } from '@/lib/models/applicantProfile';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { PdfViewerModal } from '@/components/custom-ui/PdfViewerModal';
import { CandidateEvaluationCard } from '@/components/dashboard/employer/applications/CandidateEvaluationCard';
import { OpenAnswersPanel } from '@/components/modules/OpenAnswersPanel';

interface ApplicantProfileModalProps {
    applicationId: string;
    applicantName?: string;
    roleLabel?: string; // #47b — the annons role, so the Link chip reads "Namn – Roll" (this application), not a bare name
    coverLetter?: string; // per-application personligt brev — shown here alongside the CV
    onClose: () => void;
}

const initialsOf = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
};

// #47a — human labels for a document's type, so the employer spots the CV at a
// glance among a candidate's documents.
const DOC_TYPE_LABEL: Record<string, string> = {
    cv: 'CV',
    exam_certificate: 'Examensbevis',
    grade_certificate: 'Betyg',
    other: 'Övrigt',
};

// A single labelled fact row in the profile grid.
const Fact: React.FC<{ icon: React.ComponentType<{ className?: string }>; label: string; value: React.ReactNode }> = ({
    icon: Icon,
    label,
    value,
}) => (
    <div className="flex items-start gap-2.5">
        <Icon className="w-4 h-4 text-[#1A4D4F] mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
            <p className="text-sm text-gray-800 break-words">{value}</p>
        </div>
    </div>
);

// ApplicantProfileModal — employer-facing view of a job applicant (identity +
// candidate profile + documents/CV). Fills the "review before hiring" gap (F2-1):
// the employer can inspect who they're accepting/rejecting.
const ApplicantProfileModal: React.FC<ApplicantProfileModalProps> = ({ applicationId, applicantName, roleLabel, coverLetter, onClose }) => {
    const [profile, setProfile] = useState<ApplicantProfile | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    // #47a — in-app PDF preview (e.g. the candidate's CV) via the shared viewer.
    const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);

    useEffect(() => {
        let cancelled = false;
        jobApplicationApi
            .getApplicantProfile(applicationId)
            .then((p) => { if (!cancelled) setProfile(p); })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [applicationId]);

    const name = profile?.name || applicantName || 'Sökande';
    const hoursText =
        profile && (profile.min_hours_per_week || profile.max_hours_per_week)
            ? `${profile.min_hours_per_week ?? 0}–${profile.max_hours_per_week ?? 0} tim/vecka`
            : null;
    const educationText = profile
        ? profile.education_status === 'other' && profile.other_education
            ? profile.other_education
            : EducationStatusLabels[profile.education_status ?? ''] || profile.education_status
        : null;

    return (
        <>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="w-12 h-12 ring-2 ring-white shadow-sm flex-shrink-0">
                            <AvatarImage src={resolveMediaUrl(profile?.picture_url)} alt={name} />
                            <AvatarFallback className="bg-[#1A4D4F] text-[#f5f5f5] text-sm font-semibold">
                                {initialsOf(name)}
                            </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                            <h2 className="text-base font-semibold text-gray-900 truncate">{name}</h2>
                            <p className="text-xs text-gray-400">Sökandeprofil</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                        title="Stäng"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5">
                    {isLoading ? (
                        <LoadingState message="Laddar profil..." />
                    ) : error ? (
                        <p className="text-sm text-gray-500 text-center py-6">Kunde inte ladda profilen.</p>
                    ) : profile ? (
                        <div className="space-y-5">
                            {/* Contact */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {profile.email && <Fact icon={Mail} label="E-post" value={profile.email} />}
                                {profile.phone && <Fact icon={Phone} label="Telefon" value={profile.phone} />}
                            </div>

                            {/* Candidate profile */}
                            {profile.has_profile ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                    {profile.location && <Fact icon={MapPin} label="Ort" value={profile.location} />}
                                    {educationText && <Fact icon={GraduationCap} label="Utbildning" value={educationText} />}
                                    {hoursText && <Fact icon={Clock} label="Omfattning" value={hoursText} />}
                                    {profile.available_from_date && (
                                        <Fact
                                            icon={CalendarClock}
                                            label="Tillgänglig från"
                                            value={new Date(profile.available_from_date).toLocaleDateString('sv-SE')}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                                    <User className="w-4 h-4" /> Sökanden har inte fyllt i sin profil ännu.
                                </div>
                            )}

                            {/* Personligt brev — the candidate's own words for THIS
                                application (per-application cover letter). */}
                            {coverLetter && coverLetter.trim() && (
                                <div className="space-y-2 pt-1">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
                                        <PenLine className="w-3.5 h-3.5" /> Personligt brev
                                    </p>
                                    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{coverLetter}</p>
                                    </div>
                                </div>
                            )}

                            {/* Documents */}
                            {profile.documents.length > 0 && (
                                <div className="space-y-2 pt-1">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400">Dokument</p>
                                    {profile.documents.map((doc) => (
                                        <div key={doc.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <FileText className="w-4 h-4 text-[#1A4D4F] flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <span className="text-sm text-gray-800 truncate">
                                                                {doc.original_name || doc.document_type}
                                                            </span>
                                                            {DOC_TYPE_LABEL[doc.document_type] && (
                                                                <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#1A4D4F]/10 text-[#1A4D4F]">
                                                                    {DOC_TYPE_LABEL[doc.document_type]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {doc.created_at && (
                                                            <p className="text-[11px] text-gray-400">
                                                                Uppladdad {new Date(doc.created_at).toLocaleDateString('sv-SE')}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                {doc.download_url && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setPreview({
                                                                url: resolveMediaUrl(doc.download_url!),
                                                                title: doc.original_name || DOC_TYPE_LABEL[doc.document_type] || 'Dokument',
                                                            })
                                                        }
                                                        className="inline-flex items-center gap-1 text-xs font-medium text-[#1A4D4F] hover:underline flex-shrink-0"
                                                    >
                                                        <FileText className="w-3.5 h-3.5" /> Visa
                                                    </button>
                                                )}
                                            </div>
                                            {doc.content && (
                                                <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{doc.content}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* #293 — what the applicant wrote in this job's required
                                modules. Renders nothing when the job requires no module
                                or none of them asks an open question. Placed above the
                                AI evaluation so the candidate's own words come before
                                anything written about them. */}
                            <OpenAnswersPanel
                                source={{ kind: 'application', applicationId }}
                                title="Fritextsvar från förkunskapsmoduler"
                            />

                            {/* #47b — AI evaluation (renders only when the feature flag is on) */}
                            <CandidateEvaluationCard applicationId={applicationId} applicantName={name} roleLabel={roleLabel} onOpenLink={onClose} />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
        {preview && (
            <PdfViewerModal url={preview.url} title={preview.title} onClose={() => setPreview(null)} />
        )}
        </>
    );
};

export default ApplicantProfileModal;
